/* eslint-disable @typescript-eslint/typedef */
import {
  EPlayerType,
  MouflonPlayer as MouflonPlayerConstructor,
  IStreamConfig,
  TPlayerConfigPartial,
  TWebRTCAbrPreset,
  TAdditionalFields,
} from '@streaming/mouflon-media-player';
import React, { MutableRefObject } from 'react';

import { useInterval } from './useInterval';

export type TUseMMPInternalLogicProps = {
  abrPresets?: TWebRTCAbrPreset[];
  abTests?: Record<string, string>;
  additionalMetricFields?: TAdditionalFields;
  appKey?: string;
  autoplay?: boolean;
  HLSStreamUrl?: string;
  isABREnabled: boolean;
  MediaStream?: MediaStream;
  metricsCollectionEndpoint?: string;
  metricsProjectId?: string;
  NativeStreamURL?: string;
  playerConfig?: TPlayerConfigPartial;
  playerType: EPlayerType;
  quality?: string;
  videoElement?: HTMLVideoElement;
  volume: number;
  WebRTCACLToken?: string;
  WebRTCPublishCustomData?: Record<string, unknown>;
  WebRTCSignallingUrl?: string;
  WebRTCStreamId?: string;

  /**
   * @deprecated use "quality" prop instead
   */
  WebRTCQuality?: string;
};

export const useMMPInternalLogic = ({
  HLSStreamUrl,
  WebRTCSignallingUrl,
  WebRTCPublishCustomData,
  WebRTCStreamId,
  WebRTCACLToken,
  NativeStreamURL,
  MediaStream,
  playerType,
  playerConfig,
  abTests,
  abrPresets,
  isABREnabled,
  volume,
  additionalMetricFields,
  appKey,
  metricsProjectId,
  quality,
  videoElement,
  metricsCollectionEndpoint,
  WebRTCQuality,
  playerRef,
  autoplay,
}: TUseMMPInternalLogicProps & {
  playerRef?: MutableRefObject<MouflonPlayerConstructor | null>;
}): void => {
  const mouflonPlayerRef = React.useRef<MouflonPlayerConstructor | null>(null);
  const [currentStreamConfig, setCurrentStreamConfig] = React.useState<IStreamConfig>({});

  React.useEffect(() => {
    setCurrentStreamConfig({
      HLSStreamUrl,
      WebRTCSignallingUrl,
      WebRTCPublishCustomData,
      WebRTCStreamId,
      WebRTCACLToken,
      NativeStreamURL,
      MediaStream,
    });
  }, [
    HLSStreamUrl,
    WebRTCSignallingUrl,
    WebRTCPublishCustomData,
    WebRTCStreamId,
    WebRTCACLToken,
    NativeStreamURL,
    MediaStream,
  ]);

  React.useEffect(() => {
    mouflonPlayerRef.current?.setStreamConfig(currentStreamConfig);
  }, [currentStreamConfig]);

  const isNativePlayer = playerType === EPlayerType.NATIVE;

  const retune = React.useCallback(() => {
    if (!mouflonPlayerRef.current || !playerType) {
      return;
    }

    if (isNativePlayer) {
      if (isABREnabled) {
        mouflonPlayerRef.current.enableAbr();
      } else {
        mouflonPlayerRef.current.disableAbr();
      }
    }

    if (abTests) {
      mouflonPlayerRef.current.setAbTests(abTests);
    }

    if (playerConfig) {
      mouflonPlayerRef.current.setConfig(playerConfig);
    }

    if (appKey) {
      mouflonPlayerRef.current.setAppKey(appKey);
    }

    if (videoElement) {
      mouflonPlayerRef.current.setVideoElement(videoElement);
    }

    mouflonPlayerRef.current.setStreamConfig(currentStreamConfig);
    mouflonPlayerRef.current.setPlayerType(playerType);
    mouflonPlayerRef.current.setAbrResolutions(
      abrPresets?.filter((preset) => !preset.name.includes('blurred')) || [],
    );
    if (WebRTCQuality && !isNativePlayer) {
      mouflonPlayerRef.current?.setResolutionByName(WebRTCQuality);
    }

    if (volume !== undefined) {
      mouflonPlayerRef.current?.setVolume(volume);
    }
  }, [mouflonPlayerRef.current, videoElement, playerType, isABREnabled, abrPresets, appKey]);

  React.useEffect(() => {
    let isNewlyCreated: boolean = false;

    if (
      !playerType ||
      ([EPlayerType.BROADCAST, EPlayerType.WEBRTC].includes(playerType) && !appKey)
    ) {
      return;
    }

    if (mouflonPlayerRef.current) {
      return;
    }

    if (!mouflonPlayerRef.current) {
      isNewlyCreated = true;
      mouflonPlayerRef.current = new MouflonPlayerConstructor();

      if (playerRef) {
        playerRef.current = mouflonPlayerRef.current;
      }
    }

    retune();

    if (isNewlyCreated && autoplay) {
      mouflonPlayerRef.current?.start();
    }
  }, [mouflonPlayerRef.current, videoElement, playerType, abrPresets, isABREnabled, appKey]);

  React.useEffect(() => {
    mouflonPlayerRef.current?.setVolume(volume);
  }, [volume]);

  const resetAdditionalFields = React.useCallback(() => {
    const connectionInfo: NetworkInformation | undefined = navigator.connection;

    if (connectionInfo) {
      mouflonPlayerRef.current?.setAdditionalMetricsFields({
        ...additionalMetricFields,
        reportedDownlink: connectionInfo.downlink,
        reportedRTT: connectionInfo.rtt,
        reportedNetworkType: connectionInfo.type,
      });
    }
  }, [additionalMetricFields, mouflonPlayerRef.current]);

  React.useEffect(() => {
    resetAdditionalFields();
  }, [additionalMetricFields]);

  React.useEffect(() => {
    return () => {
      mouflonPlayerRef.current?.destroy();
    };
  }, []);

  React.useEffect(() => {
    if (playerConfig) {
      mouflonPlayerRef?.current?.setConfig(playerConfig);
    }
  }, [playerConfig]);

  React.useEffect(() => {
    if (playerType) {
      mouflonPlayerRef.current?.setPlayerType(playerType);
    }
  }, [playerType]);

  React.useEffect(() => {
    if (metricsCollectionEndpoint && metricsProjectId && playerRef?.current) {
      playerRef.current?.configureMetrics(metricsCollectionEndpoint, metricsProjectId);
    }
  }, [playerRef?.current, metricsProjectId, metricsCollectionEndpoint]);

  React.useEffect(() => {
    if (videoElement) {
      mouflonPlayerRef.current?.setVideoElement(videoElement);
    }
  }, [videoElement]);

  React.useEffect(() => {
    mouflonPlayerRef.current?.setAbrResolutions(
      abrPresets?.filter((preset) => !preset.name.includes('blurred')) || [],
    );
  }, [abrPresets]);

  React.useEffect(() => {
    if (appKey) {
      mouflonPlayerRef.current?.setAppKey(appKey);
    }
  }, [appKey]);

  React.useEffect(() => {
    if (abTests) {
      mouflonPlayerRef.current?.setAbTests(abTests);
    }
  }, [abTests]);

  React.useEffect(() => {
    if (!mouflonPlayerRef.current) {
      return;
    }

    if (quality) {
      mouflonPlayerRef.current.setResolutionByName(quality);
    } else if (WebRTCQuality && playerType === EPlayerType.WEBRTC) {
      mouflonPlayerRef.current.setResolutionByName(WebRTCQuality);
    }

    if (isABREnabled) {
      mouflonPlayerRef.current.enableAbr();
    } else {
      mouflonPlayerRef.current.disableAbr();
    }
  }, [isABREnabled, WebRTCQuality, quality]);

  useInterval(resetAdditionalFields, 2000);
};
